import type { ButtonHTMLAttributes, ReactNode } from "react";
import type { Content, Tree } from "@/app/queries";

/* Общие кусочки конструктора A1: шапки карточек, чипы, «×», расчёты. */

export type Patch = (c: Content) => Content;

export const KINDS = ["видео", "курс", "статья", "конспект"] as const;

/** Цвета чипа типа ресурса — из a1.json kindMeta. */
export const KIND_LOOK: Record<string, { bg: string; fg: string }> = {
  видео: { bg: "var(--infosoft)", fg: "var(--info)" },
  курс: { bg: "var(--accsoft)", fg: "var(--acc)" },
  статья: { bg: "var(--card2)", fg: "var(--ink2)" },
  конспект: { bg: "var(--warnsoft)", fg: "var(--warn)" },
};

/** Цвета чипа роли файла — из a1.json filesPanel.roleMeta. */
export const ROLE_LOOK: Record<string, { bg: string; fg: string }> = {
  шаблон: { bg: "var(--accsoft)", fg: "var(--acc)" },
  эталон: { bg: "var(--warnsoft)", fg: "var(--warn)" },
  описание: { bg: "var(--card2)", fg: "var(--ink2)" },
  данные: { bg: "var(--infosoft)", fg: "var(--info)" },
};

export const BLOCK_DEFS = [
  { id: "quiz", tag: "проверь себя", title: "Вопросы для самопроверки", accent: "var(--info)", mono: false, hint: "вопрос и ответ; студент открывает ответ сам — в гейт не входит" },
  { id: "mistakes", tag: "типичные ошибки", title: "Типичные ошибки", accent: "var(--err)", mono: false, hint: "формат: ошибка — почему это ломает результат" },
  { id: "code", tag: "пример кода", title: "Пример кода", accent: "var(--bd2)", mono: true, hint: "подсветка синтаксиса добавится автоматически" },
  { id: "notes", tag: "заметки менторов", title: "Заметки менторов", accent: "var(--warn)", mono: false, hint: "по одной ссылке или конспекту в строке" },
] as const;

export const CRITERION_KINDS = [
  { value: "practice_share", label: "доля задач (авто)" },
  { value: "all_units", label: "все юниты (авто)" },
  { value: "question_bank", label: "банк вопросов" },
  { value: "manual", label: "отметка ментора" },
  { value: "homework", label: "домашка принята (авто)" },
] as const;

/** Пять правил готовности A1 (checklistDefs) — зеркало Content.Readiness на бэкенде;
 *  считаются от локального черновика, чтобы карточка отвечала на правку сразу. */
export function readiness(c: Content): { rules: { text: string; ok: boolean }[]; ready: number; total: number } {
  // Первоисточники (optional) вне гейта — правила смотрят только на обязательные.
  const req = c.resources.filter((r) => !r.optional);
  const allGuided = req.length > 0 && req.every((r) => r.units.length > 0);
  const rules = [
    { text: "Минимум 3 ресурса", ok: req.length >= 3 },
    { text: "У каждого ресурса размечен гайд по юнитам", ok: allGuided },
    { text: "Практика: оба уровня не пусты", ok: c.practice.base.length > 0 && c.practice.apply.length > 0 },
    { text: "Блоки «проверь себя» и «типичные ошибки» включены", ok: !!c.blocks.quiz?.on && !!c.blocks.mistakes?.on },
    { text: "Критериев гейта не меньше трёх", ok: c.gate.criteria.length >= 3 },
  ];
  return { rules, ready: rules.filter((r) => r.ok).length, total: rules.length };
}

/** Четыре блока из BLOCK_DEFS плюс «Если застрял» (content.stuck). */
export const BLOCKS_TOTAL = BLOCK_DEFS.length + 1;

export function blocksOn(c: Content): number {
  return BLOCK_DEFS.filter((b) => c.blocks[b.id]?.on).length + (c.stuck?.on ? 1 : 0);
}

type Block = Content["blocks"][string];
export type QA = NonNullable<Block["items"]>[number];

/** Вопросы «Проверь себя»: items, а пока их нет — старый text построчно
 *  (сервер мигрирует так же; ответы у таких вопросов пусты). */
export function quizItems(block: Block | undefined): QA[] {
  if (!block) return [];
  if (block.items && block.items.length > 0) return block.items;
  return block.text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .map((q) => ({ id: "", q, a: "" }));
}

/** Проверено больше 6 месяцев назад — ресурс пора перепроверить. */
export function isStale(checkedOn: string | undefined, now: Date = new Date()): boolean {
  if (!checkedOn || !/^\d{4}-\d{2}-\d{2}$/.test(checkedOn)) return false;
  const edge = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 6, now.getUTCDate()));
  return checkedOn < edge.toISOString().slice(0, 10);
}

/** «проверено 09.2026» — как «актуально на MM.YYYY» у студента. */
export function checkedLabel(checkedOn: string | undefined): string {
  if (!checkedOn || !/^\d{4}-\d{2}-\d{2}$/.test(checkedOn)) return "";
  return `проверено ${checkedOn.slice(5, 7)}.${checkedOn.slice(0, 4)}`;
}

/** Сегодня в формате поля даты (YYYY-MM-DD, локальная дата). */
export function todayISO(now: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

/** Размер файла по правилу макета: ≥ 1 МБ — с десятыми, иначе целые КБ. */
export function fmtSize(bytes: number): string {
  const kb = bytes / 1024;
  return kb >= 1024 ? `${(kb / 1024).toFixed(1)} МБ` : `${Math.round(kb)} КБ`;
}

/** Положение шага в дереве: трек, индекс, соседи. */
export function locate(tree: Tree, stepId: string) {
  const tracks = [...tree.tracks].sort((a, b) => a.position - b.position);
  for (let ti = 0; ti < tracks.length; ti++) {
    const track = tracks[ti]!;
    const si = track.steps.findIndex((s) => s.id === stepId);
    if (si < 0) continue;
    const prevInTrack = track.steps[si - 1];
    const nextInTrack = track.steps[si + 1];
    const chain = tracks.filter((t) => !t.parallel);
    const ci = chain.findIndex((t) => t.id === track.id);
    const prevTrack = track.parallel ? undefined : chain.slice(0, ci).reverse().find((t) => t.steps.length > 0);
    const nextTrack = track.parallel ? undefined : chain.slice(ci + 1).find((t) => t.steps.length > 0);
    return { track, index: si, prevInTrack, nextInTrack, prevTrack, nextTrack };
  }
  return null;
}

export function CardHead({ title, meta, right, className = "" }: { title: string; meta?: ReactNode; right?: ReactNode; className?: string }) {
  return (
    <div className={`flex items-center gap-2.5 border-b border-bd bg-bg2 px-3.5 py-[11px] ${className}`}>
      <span className="font-mono text-[11px] uppercase tracking-[.06em] text-mut">{title}</span>
      {meta && <span className="font-mono text-[11px] text-mut">{meta}</span>}
      <span className="flex-1" />
      {right}
    </div>
  );
}

/** Подпись-заголовок карточки без подложки (aside). */
export function AsideHead({ title, right }: { title: string; right?: ReactNode }) {
  return (
    <div className="mb-[11px] flex items-center gap-2.5">
      <span className="font-mono text-[11px] uppercase tracking-[.06em] text-mut">{title}</span>
      <span className="flex-1" />
      {right}
    </div>
  );
}

export function Chip({ look, children }: { look: { bg: string; fg: string }; children: ReactNode }) {
  return (
    <span className="w-fit rounded-sm px-2 py-[3px] font-mono text-[10.5px]" style={{ background: look.bg, color: look.fg, border: `1px solid ${look.fg}` }}>
      {children}
    </span>
  );
}

/** Кнопка-иконка «×» / «+» / «−»: цвет задан явно (правило CLAUDE.md). */
export function IconButton({ className = "", ...rest }: ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button type="button" className={`cursor-pointer border-0 bg-transparent px-1 font-mono text-[12px] leading-none text-mut hover:text-ink ${className}`} {...rest} />;
}

/** Пунктирная кнопка «+ …» — добавление элемента. */
export function DashedButton({ className = "", ...rest }: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={`cursor-pointer rounded-sm border border-dashed border-bd2 bg-transparent px-[11px] py-[6px] text-left font-mono text-[11px] text-mut hover:border-acc hover:text-acc ${className}`}
      {...rest}
    />
  );
}

export const INPUT = "rounded-sm border border-bd2 bg-bg2 px-[11px] py-2 text-ink outline-none focus:border-acc";
export const GHOST_INPUT = "w-full rounded-sm border border-transparent bg-transparent px-1.5 py-1 text-ink outline-none focus:border-acc focus:bg-bg2";
